import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import './css/index.css';
import './css/app.css';
import { ThemeProvider } from './theme';
import { AuthProvider, useAuth } from './AuthContext';
import { ExplainProvider } from './ExplainContext';
import { LensWeightsProvider } from './lensWeights';
import { ConfirmHost } from './Confirm';
import About from './About';
import CreateObject from './CreateObject';
import Triage from './Triage';
import ObjectDetail from './ObjectDetail';
import Login from './Login';
import Register from './Register';
import TrustAtlas from './TrustAtlas';
import TrustInspect from './TrustInspect';
import TrustDomain from './TrustDomain';
import CompareView from './CompareView';
import Argument from './Argument';
import ActorTimeline from './ActorTimeline';
import Read from './Read';
import Queue from './Queue';
import Settings from './Settings';
import Groups from './Groups';
import Write from './Write';
import Thread, { ThreadList } from './Thread';

// ClaimView merged into ObjectDetail (UX rebuild wave 1); old links keep working.
function ClaimRedirect() {
  const { id } = useParams();
  return <Navigate to={`/object/${id}`} replace />;
}

// Viewsets merged into Home (owner ruling 2026-08-05): the pill row selects,
// ?viewset= carries the selection, editing is the inline ⚙ fold. Old links
// keep working, same pattern as /claim/:id.
function ViewsetRedirect() {
  const { id } = useParams();
  return <Navigate to={`/?viewset=${id}`} replace />;
}

// Add merged into Write (owner ruling 2026-08-07): one door for words. The
// query string MUST survive the redirect — the clipper extension and the
// planned OS share target hand off via /dump?url= / ?clip=&clip_source=.
function DumpRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/write${search}`} replace />;
}

// /resolve and Browse folded into Home (2026-08-07, HANDOFF 0c). Neither was a
// page: /resolve was the front door under the `contested` ranking rule, and
// Browse was the front door with no filter under `new`. Both redirects carry
// the meaning of the page they replace rather than only its address —
// /resolve?viewset=X keeps landing on X, ranked the way /resolve ranked it,
// because contested is Home's default rule.
function ResolveRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (!params.get('viewset')) params.set('view', 'everything');
  return <Navigate to={`/?${params.toString()}`} replace />;
}

function BrowseRedirect() {
  return <Navigate to="/?view=everything&sort=new" replace />;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
}

function Root() {
  return (
    <Routes>
      {/* The ONE front door: a pill row (the filter) over a ranking rule.
          The reading queue is still the default pill (owner decision
          2026-07-05: lobby-first); `everything` and each viewset are the
          others, and the tension-ranked resolve stream is now the `contested`
          rule rather than an address. */}
      <Route path="/" element={<Queue />} />
      <Route path="/resolve" element={<ResolveRedirect />} />
      <Route path="/queue" element={<Navigate to="/" replace />} />
      <Route path="/browse" element={<BrowseRedirect />} />
      <Route path="/about" element={<About />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/create" element={<ProtectedRoute><CreateObject /></ProtectedRoute>} />
      <Route path="/dump" element={<DumpRedirect />} />
      {/* The Writer, and since 2026-08-07 THE one door for words (DECISIONS.md
          "Write and Add merge"): composition + ingestion in one box. */}
      <Route path="/write" element={<ProtectedRoute><Write /></ProtectedRoute>} />
      <Route path="/triage" element={<ProtectedRoute><Triage /></ProtectedRoute>} />
      <Route path="/object/:id" element={<ObjectDetail />} />
      <Route path="/viewsets" element={<Navigate to="/" replace />} />
      <Route path="/viewsets/:id" element={<ViewsetRedirect />} />
      <Route path="/trust" element={<TrustAtlas />} />
      <Route path="/trust/inspect" element={<TrustInspect />} />
      <Route path="/trust/:tagId" element={<TrustDomain />} />
      <Route path="/claim/:id" element={<ClaimRedirect />} />
      <Route path="/compare" element={<CompareView />} />
      <Route path="/argument/:id" element={<Argument />} />
      <Route path="/actor/:id" element={<ActorTimeline />} />
      <Route path="/read/:id" element={<Read />} />
      {/* NOT ProtectedRoute, deliberately (2026-08-06): a thread is meant to be
          handed to someone as a link, so the page must render for a signed-out
          reader. The bound is in the backend queries — an anonymous session
          resolves to the 'all' group — never in a login wall here. */}
      <Route path="/thread/:id" element={<Thread />} />
      <Route path="/threads" element={<ThreadList />} />
      {/* Groups (2026-08-13): the management surface for the middle value of
          the human access axis — the page AccessControl's empty state points
          at. A group is a name and a member list; no roles. */}
      <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
    </Routes>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ExplainProvider>
            <LensWeightsProvider>
              <Root />
              <ConfirmHost />
            </LensWeightsProvider>
          </ExplainProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);
